import { getExaminerProfileThunk } from '../../../../../store/workspace/thunks';
import { useDispatch, useSelector } from '../../../../../store/store';
import { examStatusTitle } from '../../../../../utils/generalHelpers';
import CheckBox from '../../../../../components/CheckBox';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import {
    useState,
    forwardRef,
    ForwardedRef,
} from 'react';
import {
    ProfileIcon,
    DocumentIcon,
    CheckSolidIcon,
    TimesSolidIcon,
    ChevronDownIcon,
    NotApplicableIcon,
} from '../../../../../assets/icons';
import {
    EvaluatedCriteria,
    RecruitingInterviewStatics,
    RecruitingInterviewExaminerData,
} from '../../../../../store/workspace/types';
import {
    selectSideBarExpanded,
    selectSelectedApplicantsIds,
} from '../../../../../store/workspace/selectors';
import {
    setExaminerProfileModal,
    setSelectedApplicantsIds,
    setSelectedInterviewApplicant,
    setRecruitingInterviewReportModal,
} from '../../../../../store/workspace/slice';

const columnKeys = [
    'culturalFit',
    'communicationSkills',
    'problemSolvingAbility',
    'teamCollaboration',
    'timeManagement',
    'selfMotivation',
    'multitaskingAbility',
    'taskPrioritization',
    'productivity',
];

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    statics: RecruitingInterviewStatics | null;
};

const RecruitingInterviewTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { jobId, statics, selectable } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isSidebarExpanded = useSelector(selectSideBarExpanded);
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);
    const [expandedCards, setExpandedCards] = useState<string[]>([]);

    const toggleCard = (userId: string) => {
        setExpandedCards(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const openReport = (res: EvaluatedCriteria) => {
        dispatch(setSelectedInterviewApplicant(res))
        dispatch(setRecruitingInterviewReportModal(true));
    };

    const getExaminerProfile = (id: string) => {
        dispatch(getExaminerProfileThunk({ id, jobId }));
        dispatch(setExaminerProfileModal(true));
    };

    const renderResult = (res: 'True' | 'False' | 'Not Applicable') => {
        switch (res) {
            case 'True':
                return <CheckSolidIcon />;
            case 'False':
                return <TimesSolidIcon />;
            case 'Not Applicable':
                return <NotApplicableIcon />;
            default:
                return ''
        }
    };

    const navigateToResults = (applicant: RecruitingInterviewExaminerData) => {
        if (!applicant.answerAnalysis) return;
        navigate(`${applicant.userId}`);
    };

    const selectExaminer = (examinerId: string) => {
        const check = selectedApplicantsIds.some(i => i === examinerId);
        let list = [...selectedApplicantsIds];

        if (check) {
            list = list.filter(i => i !== examinerId);
        } else {
            list.push(examinerId);
        }

        dispatch(setSelectedApplicantsIds(list));
    };

    const selectAllExaminers = () => {
        const check = selectedApplicantsIds.length === statics?.examiners?.length;
        const newList = check ? [] : statics?.examiners?.map(i => i.userId) || [];
        dispatch(setSelectedApplicantsIds(newList));
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.table} ref={ref}>
                <table>
                    <thead>
                        {selectable && <tr>
                            <th colSpan={12}>
                                <div className={styles.select_all}>
                                    <CheckBox
                                        onClick={selectAllExaminers}
                                        checked={statics?.examiners?.length !== 0 && statics?.examiners?.length === selectedApplicantsIds.length}
                                    />
                                    <span>Select All ({statics?.examiners?.length})</span>
                                </div>
                            </th>
                        </tr>}
                        <tr data-highlight="true">
                            <th>Name</th>
                            {!isSidebarExpanded && <th>Status</th>}
                            <th>Cultural<br />Fit</th>
                            <th>Communication<br />Skills</th>
                            <th>Problem-Solving<br />Ability</th>
                            <th>Team<br />Collaboration</th>
                            <th>Time<br />Management</th>
                            <th>Self<br />Motivation</th>
                            <th>Multitasking<br />Ability</th>
                            <th>Task<br />Prioritization</th>
                            <th>Productivity</th>
                            <th data-noprint="true">Questions<br />& feedbacks</th>
                            <th data-noprint="true">Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {statics?.examiners.map(applicant => <tr key={applicant.userId}>
                            <td>
                                <div className={styles.examinerName}>
                                    {selectable && <CheckBox
                                        onClick={() => selectExaminer(applicant.userId)}
                                        checked={selectedApplicantsIds.some(i => i === applicant.userId)}
                                    />}
                                    <span>{applicant.first_name} {applicant.last_name}</span>
                                </div>
                            </td>
                            {!isSidebarExpanded && <td>
                                <span
                                    className={styles.status}
                                    data-status={examStatusTitle(applicant.status)}
                                >
                                    {examStatusTitle(applicant.status)}
                                </span>
                            </td>}
                            {columnKeys.map((col) => {
                                const matchedItem = applicant.evaluatedCriteria?.find((item) => item.nameKey === col);
                                return <td key={col}>{matchedItem ?
                                    <div className={styles.group} onClick={() => openReport(matchedItem)}>
                                        {renderResult(matchedItem.result)}
                                        <span>Report</span>
                                    </div> : 'In progress'}</td>
                            })}
                            <td data-noprint="true"><DocumentIcon onClick={() => navigateToResults(applicant)} /></td>
                            <td data-noprint="true"><ProfileIcon onClick={() => getExaminerProfile(applicant.userId)} /></td>
                        </tr>)}
                        {statics && statics.examiners.length === 0 && <tr><td colSpan={12} style={{ textAlign: 'center' }}>No data.</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {statics?.examiners.map(applicant => {
                    const isExpanded = expandedCards.includes(applicant.userId);
                    const culturalFit = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'culturalFit');
                    const communicationSkills = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'communicationSkills');
                    const problemSolvingAbility = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'problemSolvingAbility');
                    const teamCollaboration = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'teamCollaboration');
                    const timeManagement = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'timeManagement');
                    const selfMotivation = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'selfMotivation');
                    const multitaskingAbility = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'multitaskingAbility');
                    const taskPrioritization = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'taskPrioritization');
                    const productivity = applicant.evaluatedCriteria?.find((item) => item.nameKey === 'productivity');

                    return (
                        <div className={styles.card} key={applicant.userId}>
                            <div className={styles.card_header} onClick={() => toggleCard(applicant.userId)}>
                                <span>{applicant.first_name} {applicant.last_name}</span>
                                <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                            </div>
                            {isExpanded && <div className={styles.card_body}>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        Criteria
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Cultural Fit</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(culturalFit?.result || 'Not Applicable')}
                                                {culturalFit && <span onClick={() => openReport(culturalFit)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Communication Skills</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(communicationSkills?.result || 'Not Applicable')}
                                                {communicationSkills && <span onClick={() => openReport(communicationSkills)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Problem-Solving Ability</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(problemSolvingAbility?.result || 'Not Applicable')}
                                                {problemSolvingAbility && <span onClick={() => openReport(problemSolvingAbility)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Team Collaboration</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(teamCollaboration?.result || 'Not Applicable')}
                                                {teamCollaboration && <span onClick={() => openReport(teamCollaboration)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Time Management</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(timeManagement?.result || 'Not Applicable')}
                                                {timeManagement && <span onClick={() => openReport(timeManagement)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Self Motivation</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(selfMotivation?.result || 'Not Applicable')}
                                                {selfMotivation && <span onClick={() => openReport(selfMotivation)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Multitasking Ability</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(multitaskingAbility?.result || 'Not Applicable')}
                                                {multitaskingAbility && <span onClick={() => openReport(multitaskingAbility)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Task Prioritization</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(taskPrioritization?.result || 'Not Applicable')}
                                                {taskPrioritization && <span onClick={() => openReport(taskPrioritization)}>Report</span>}
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section_content_item}>
                                            <span>Productivity</span>
                                            <div className={styles.card_body_section_content_item_result}>
                                                {renderResult(productivity?.result || 'Not Applicable')}
                                                {productivity && <span onClick={() => openReport(productivity)}>Report</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </div>
                    )
                })}
            </div>
        </div>
    )
});

export default RecruitingInterviewTable;
